import React from "react";
import ReactDOM from "react-dom";
import { Button } from "../Button";
import style from './style/Modal.module.scss'
import Loader from "../Loader";
import '../../styles/palette.scss'

export const Modal: React.FC<ModalProps> = ({
  id,
  lockConfirm,
  handleCancelClick,
  isOpen,
  onClose,
  children,
  title,
  isActionModal= false,
  formId,
  handleOkClick,
  okText,
  cancelText= okText,
  classes,
  isSaving,
}) => {
  if (!isOpen) return null;

  const handleOverlayClick = (event: React.MouseEvent) => {
    event.stopPropagation()
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  const handleModalClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  const okLabel = isSaving ? <Loader loaderSize={20} loaderColor={"#FFF"} relative /> : okText;

  const element = (<div className={`${style.overlay as string} ${classes?.overlay ?? ""}`} onClick={handleOverlayClick} data-testid="modal-overlay">
    <div id={id} className={`${style.modal as string} ${classes?.modal ?? ""}`} onClick={handleModalClick}>
      <div className={`${style.header as string} ${classes?.header ?? ""}`}>
        {typeof title === "string" ? <h2>{title}</h2> : title}
        <button type="button" onClick={onClose}>
          <span aria-hidden="true">×</span>
        </button>
      </div>
      {children}
      <div className={`${isActionModal ? style.footer__action : style.footer} ${classes?.footer ?? ""}`}>
        <Button
            data-testid="cancel-button"
            buttonStyle="secondary"
            label={cancelText}
            onClick={handleCancelClick != null ? handleCancelClick : onClose}
        />
        {isActionModal && <Button
            data-testid="confirm-button"
            buttonStyle="primary"
            disabled={lockConfirm}
            label={okLabel}
            form={formId}
            type={(formId != null) ? "submit" : "button"}
            onClick={(formId != null) ? null : handleOkClick}
        />}
      </div>
    </div>
  </div>)

  return ReactDOM.createPortal(element, document.getElementById("modal-root") as HTMLElement // Ensure modal-root exists
  );
};

export interface ModalProps {
  /**
   * The id of the Modal.
   */
  id?: string;

  /**
   * The content to be displayed inside the Modal.
   */
  children?: React.ReactNode;

  /**
   * Determines the open/closed state of the Modal.
   * @default false
   */
  isOpen: boolean;

  /**
   * Callback function to close the Modal - should set `isOpen` to false.
   */
  onClose: () => void;

  /**
   * Optional title for the Modal.
   */
  title?: string | React.ReactElement;

  /**
   * Whether the Modal is in action mode (e.g., with confirm/cancel buttons).
   * @default false
   */
  isActionModal: boolean;

  /**
   * Form ID for the modal if the modal is part of a form.
   * @default ""
   */
  formId?: string;

  /**
   * Whether the Confirm button should be locked (disabled).
   * @default false
   */
  lockConfirm: boolean;

  /**
   * The text to display for the Cancel button.
   * @default "Cancel"
   */
  cancelText: string;

  /**
   * The text to display for the Ok/Confirm button.
   * @default "Ok"
   */
  okText?: string;

  /**
   * Optional handler for when the Cancel button is clicked.
   */
  handleCancelClick?: () => void;

  /**
   * Optional handler for when the Ok/Confirm button is clicked.
   */
  handleOkClick?: (() => void) | null;

  /**
   * An object representing the CSS classes to apply for styling the component.
   * It can include optional properties for the overlay, the modal or the header.
   */
  classes?: {
    modal?: string;
    overlay?: string;
    header?: string;
    footer?: string;
  };

  /**
   * to indicate to show loader
   */
  isSaving?: boolean;
}

export default Modal;
